import {
  FIELD_KEYS,
  TRIGGER_FIELDS_CONFIG_MAP,
  PAYLOAD_MAP,
  COMPONENT_TYPES,
  OPERATORS_TO_COMBINATIONS_MAP,
  DIVIDER,
  emailRegex,
} from "./constants.js";

const NESTED_CONFIG_COUNT = 3; // Change this number to reflect the amount of SubSubConfiguration${index} fields
const NESTED_CONFIG_TEMPLATE = new Array(NESTED_CONFIG_COUNT).fill(NESTED_CONFIG_COUNT);

export const fillNestedConfigPayload = () => {
  return NESTED_CONFIG_TEMPLATE.map((_, i) => `${FIELD_KEYS.SubSubConfiguration}${i + 1}`);
};

export const fillNestedConfigModel = (defaultValue) => {
  return NESTED_CONFIG_TEMPLATE.reduce((acc, _, i) => {
    acc[`${FIELD_KEYS.SubSubConfiguration}${i + 1}`] = defaultValue;
    return acc;
  }, {});
};  

export const getPartsFromCombinedValue = (combinedValue) => {
  // TODO: Workaround
  const resolvedValue = Array.isArray(combinedValue)
    ? combinedValue[0]?.id
    : combinedValue
  const [id, configId, configValue] = resolvedValue.split(DIVIDER);
  return { id, configId, configValue };
};

export const getConfigKeys = (field) => {
  const { idKey, configIdKey, configValueKey, endpoint } = TRIGGER_FIELDS_CONFIG_MAP[field];
  return { idKey, configIdKey, configValueKey, endpoint };
};

export const getNextKey = (field, currentKey) => {
  const keys = PAYLOAD_MAP.get(field);
  const currentKeyIndex = keys.findIndex(el => el === currentKey);
  const nextKey = keys[currentKeyIndex + 1];
  return nextKey ?? null;
};

export const getPrevKey = (field, currentKey) => {
  const keys = PAYLOAD_MAP.get(field);
  const currentKeyIndex = keys.findIndex(el => el === currentKey);
  const nextKey = keys[currentKeyIndex - 1];
  return nextKey ?? null;
};

export const getSubSubConfigCombinedValue = (item, index) => {
  const { SubConfiguration, SubSubConfiguration } = FIELD_KEYS;
  return index > 0
    ? item[`${SubSubConfiguration}${index}`]
    : item[SubConfiguration];
};

export const getSubSubConfigCurrentKey = (index) => {
  const { SubConfiguration, SubSubConfiguration } = FIELD_KEYS;
  return index > 0
    ? `${SubSubConfiguration}${index}`
    : SubConfiguration;
};

export const mapObjectOptions = (items, field, key) => {
  const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

  return items[key].map(el => {
    const combinedValue = [el?.[idKey], el?.[configIdKey], el?.[configValueKey]].join(DIVIDER);
    return {
      id: combinedValue,
      value: combinedValue,
      label: el[configValueKey],
    };
  }) ?? [];
};

export const mapArrayOptions = (items, field, key, index) => {
  const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

  return items?.[index]?.[key]?.map(el => {
    const combinedValue = [el?.[idKey], el?.[configIdKey], el?.[configValueKey]].join(DIVIDER);
    return {
      id: combinedValue,
      value: combinedValue,
      label: el?.[configValueKey],
      name: el?.[configValueKey],
    };
  }) ?? [];
};

export const mapMultiSelectValue = (item, field) => {
  const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

  return item.selected_conditions.map(el => {
    const combinedValue = [el?.[idKey], el?.[configIdKey], el?.[configValueKey]].join(DIVIDER);

    return {
      label: el?.[configValueKey],
      name: el?.[configValueKey],
      option_label: el?.[configValueKey],
      id: combinedValue,
      value: combinedValue,
      option_value: combinedValue,
    };
  });
};

export const mapMultiSelectOptions = (item, field) => {
  const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

  return item.selected_conditions.map(el => ({
    [idKey]: item[idKey],
    [configIdKey]: el?.[configIdKey],
    [configValueKey]: el?.[configValueKey],
    has_child: false,
  }));
};

export const mapMultiselectGroupedValue = (item, field) => {
  const { idKey, configIdKey } = getConfigKeys(field);
  const parsedArray = getArrayFromString(item[configIdKey])

  return parsedArray.map(el => {
    const combinedValue = [item?.[idKey], el, el].join(DIVIDER);

    return {
      label: el,
      name: el,
      option_label: el,
      id: combinedValue,
      value: combinedValue,
      option_value: combinedValue,
    };
  }); 
};

export const mapMultiselectGroupedOptions = (item, field) => {
  const { idKey, configIdKey, configValueKey } = getConfigKeys(field);
  const parsedArray = getArrayFromString(item[configIdKey]);

  return parsedArray.map(el => {
    return {
      [idKey]: item[idKey],
      [configIdKey]: el,
      [configValueKey]: el,
      has_child: false,
    };
  });
};

export const getArrayFromString = (str) => {
  try {
    return JSON.parse(str?.replace(/'/g, '"')) ?? [];
  } catch (error) {
    console.error("Error parsing string to array:", error);
    return [];
  }
};

export const validateFieldData = (fieldData = {}) => {
  let result = false;

  if (Array.isArray(fieldData.model)) {
    for (const [index, row] of fieldData.model.entries()) {
      for (const key in row) {
        if (key === "_itemId") continue;

        if (fieldData?.components?.[index]?.[key] === COMPONENT_TYPES.Range) {  
          const [min, max] = row[key].range ?? [];
          if (Number(min) >= Number(max) || !min.length || !max.length) {
            result = true;
            break;
          }
        } else if (fieldData?.components?.[index]?.[key] === COMPONENT_TYPES.Email) {
          if (!row[key].text.length || !emailRegex.test(row[key].text)) {
            result = true;
            break;
          }
        } else if (fieldData?.delay?.[index]?.isDelayed && fieldData?.delay?.[index]?.isInValid) {
          result = true;
          break;
        } if ((typeof row[key] === "string" || Array.isArray(row[key])) && !row[key].length && fieldData.options[index][key].length > 0) {
          result = true;
          break;
        }
      }
    }
  } else {
    for (const key in fieldData.model) {
      if (key === "_itemId") continue;
      if (!fieldData.model[key].length && fieldData.options[key].length > 0) {
        result = true;
        break;
      }
    }
  }

  return result;
};

export const getSubSubConfigsFromItem = (item) => {
  return Object.keys(item).filter(key => key.includes(FIELD_KEYS.SubSubConfiguration));
};

export const getOperatorFromCombinations = (combination) => {
  const { main, additional } = combination;
  for (const [key, value] of Object.entries(OPERATORS_TO_COMBINATIONS_MAP)) {
    if (value.main === main && value.additional === additional) {
      return key;
    }
  }
};

export const getCombinationFromOperator = (operator) => {
  const { main, additional } = OPERATORS_TO_COMBINATIONS_MAP[operator];
  return { main, additional };
};
